import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PaperContainer } from '../shared/components/containers/PaperContainer';
import { PasswordInput } from '../shared/components/inputs/PasswordInput';
import { PrimaryButton } from '../shared/components/buttons/PrimaryButton';
import { useEncryption } from '../context/EncryptionContext';

const GateContainer = styled(PaperContainer)`
  max-width: 400px;
  padding: 20px 30px;
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 1.2em;
  margin-bottom: 5px;
`;

const Explanation = styled(Form.Text)`
  margin-bottom: 15px;
  display: block;
`;

const StyledPasswordInput = styled(PasswordInput)`
  width: 100%;
  margin-bottom: 15px;
`;

const ErrorText = styled(Form.Text)`
  color: ${(props) => props.theme.colors.error};
  display: block;
  margin-bottom: 15px;
`;

interface OwnProps {
  children?: ReactNode;
}

const EncryptionGate = (props: OwnProps) => {
  const { children } = props;
  const { t } = useTranslation();
  const { loading, needsSetup, locked, setup, unlock } = useEncryption();
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return <PaperContainer>{t('common.loading')}</PaperContainer>;
  }

  if (needsSetup) {
    const onSetupClick = async () => {
      if (passphrase.length < 8) {
        setError(t('encryptionGate.setup.errors.tooShort'));
        return;
      }
      if (passphrase !== confirmPassphrase) {
        setError(t('encryptionGate.setup.errors.mismatch'));
        return;
      }
      setError(null);
      setSubmitting(true);
      try {
        await setup(passphrase);
      } catch {
        setError(t('encryptionGate.setup.errors.failed'));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <GateContainer>
        <Header>{t('encryptionGate.setup.heading')}</Header>
        <Explanation muted>
          {t('encryptionGate.setup.explanation')}
        </Explanation>
        <Form.Label>{t('encryptionGate.setup.passphraseLabel')}</Form.Label>
        <StyledPasswordInput onChange={setPassphrase} value={passphrase}/>
        <Form.Label>{t('encryptionGate.setup.confirmPassphraseLabel')}</Form.Label>
        <StyledPasswordInput onChange={setConfirmPassphrase} value={confirmPassphrase}/>
        {error && <ErrorText>{error}</ErrorText>}
        <PrimaryButton onClick={onSetupClick} disabled={submitting}>
          {submitting ? t('encryptionGate.setup.submitting') : t('encryptionGate.setup.submit')}
        </PrimaryButton>
      </GateContainer>
    );
  }

  if (locked) {
    const onUnlockClick = async () => {
      setError(null);
      setSubmitting(true);
      try {
        await unlock(passphrase);
      } catch {
        setError(t('encryptionGate.unlock.errors.wrongPassphrase'));
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <GateContainer>
        <Header>{t('encryptionGate.unlock.heading')}</Header>
        <Explanation muted>{t('encryptionGate.unlock.explanation')}</Explanation>
        <Form.Label>{t('encryptionGate.unlock.passphraseLabel')}</Form.Label>
        <StyledPasswordInput onChange={setPassphrase} value={passphrase}/>
        {error && <ErrorText>{error}</ErrorText>}
        <PrimaryButton onClick={onUnlockClick} disabled={submitting}>
          {submitting ? t('encryptionGate.unlock.submitting') : t('encryptionGate.unlock.submit')}
        </PrimaryButton>
      </GateContainer>
    );
  }

  return <>{children}</>;
};

export {
  EncryptionGate,
};
